import { ISurveyQuestionGroupItem, ISurveyQuestionOption, IVisibilityCondition } from '../../services/survey/survey.model';

/**
 * The one vocabulary for answer conditions, shared by question display conditions
 * and rule "only calculate when" conditions.
 */
export const OP_LABELS: Record<IVisibilityCondition['op'], string> = {
  answered: 'is answered',
  equals: '=',
  notEquals: '≠',
  in: 'is one of',
  gt: '>',
  lt: '<',
  gte: '≥',
  lte: '≤',
};

export const ALL_CONDITION_OPS = Object.keys(OP_LABELS) as IVisibilityCondition['op'][];

/** Select value meaning "any option" when the operator is `answered` on a choice question. */
export const ANSWERED_ANY = '__any__';

export type ChoiceOptionsLookup = (questionId: string) => ISurveyQuestionOption[];

export function choiceOptionsLookupFor(items: ISurveyQuestionGroupItem[]): ChoiceOptionsLookup {
  return (questionId: string) =>
    items.find(i => i.surveyQuestionId === questionId)?.surveyQuestion?.surveyQuestionOptions ?? [];
}

/**
 * Normalises freshly edited conditions into the persisted shape:
 * "answered" with a chosen option becomes an equals check, "in" values are
 * split from comma text, and numeric comparisons are coerced to numbers.
 */
export function sanitizeConditions(
  conditions: IVisibilityCondition[],
  choiceOptions: ChoiceOptionsLookup,
): IVisibilityCondition[] {
  return conditions.map((c): IVisibilityCondition => {
    if (c.op === 'answered') {
      const opts = choiceOptions(c.questionId);
      if (opts.length > 0 && typeof c.value === 'string' && c.value.trim() !== '' && c.value !== ANSWERED_ANY) {
        return { questionId: c.questionId, op: 'equals', value: c.value };
      }
      return { questionId: c.questionId, op: 'answered' };
    }
    if (c.op === 'in') {
      const v = c.value;
      const arr = Array.isArray(v) ? v : typeof v === 'string' ? v.split(',').map(s => s.trim()).filter(Boolean) : [];
      return { questionId: c.questionId, op: 'in', value: arr };
    }
    if (c.op === 'gt' || c.op === 'lt' || c.op === 'gte' || c.op === 'lte') {
      const n = typeof c.value === 'number' ? c.value : Number(c.value);
      return { questionId: c.questionId, op: c.op, value: Number.isFinite(n) ? n : 0 };
    }
    return { questionId: c.questionId, op: c.op, value: c.value as string | number | string[] };
  });
}
